/**
 * Deep Link Handler Service
 * Handles incoming deep links and routes them appropriately
 */

import { App } from '@capacitor/app';
import { isAppDeepLink, parseDeepLink } from '@/utils/authRedirectUtils';
import { isIOSNativeApp, isAndroidNativeApp } from '@/utils/platformUtils';
import { ReferralStorageService } from '@/services/referral/ReferralStorageService';
import { supabase } from '@/integrations/supabase/client';

export interface DeepLinkEvent {
  url: string;
  path: string;
  params: Record<string, string>;
}

export type DeepLinkHandler = (event: DeepLinkEvent) => void;

class DeepLinkHandlerService {
  private handlers: Map<string, DeepLinkHandler[]> = new Map();
  private isInitialized = false;

  /**
   * Initialize the deep link handler service
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    // Only initialize on native platforms
    if (!isIOSNativeApp() && !isAndroidNativeApp()) {
      console.log('🔗 Deep link handler: Not a native platform, skipping initialization');
      return;
    }

    try {
      // Listen for app URL open events
      App.addListener('appUrlOpen', (event) => {
        this.handleIncomingUrl(event.url);
      });

      // Check if app was launched with a URL
      const launchUrl = await App.getLaunchUrl();
      if (launchUrl?.url) {
        console.log('🔗 App launched with URL:', launchUrl.url);
        // Delay processing to allow app to fully initialize
        setTimeout(() => {
          this.handleIncomingUrl(launchUrl.url);
        }, 1000);
      }

      this.isInitialized = true;
      console.log('🔗 Deep link handler initialized');
    } catch (error) {
      console.error('🔗 Error initializing deep link handler:', error);
    }
  }

  /**
   * Register a handler for a specific path pattern
   * @param pathPattern - The path pattern to match (e.g., 'auth/callback', 'signup')
   * @param handler - The handler function to call
   */
  registerHandler(pathPattern: string, handler: DeepLinkHandler): void {
    if (!this.handlers.has(pathPattern)) {
      this.handlers.set(pathPattern, []);
    }
    this.handlers.get(pathPattern)!.push(handler);
    console.log(`🔗 Registered deep link handler for pattern: ${pathPattern}`);
  }

  /**
   * Unregister a handler for a specific path pattern
   * @param pathPattern - The path pattern
   * @param handler - The handler function to remove
   */
  unregisterHandler(pathPattern: string, handler: DeepLinkHandler): void {
    const handlers = this.handlers.get(pathPattern);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
        console.log(`🔗 Unregistered deep link handler for pattern: ${pathPattern}`);
      }
    }
  }

  /**
   * Handle an incoming URL
   * @param url - The incoming URL to process
   */
  private handleIncomingUrl(url: string): void {
    console.log('🔗 Processing incoming URL:', url);

    // Check if this is an app deep link
    if (!isAppDeepLink(url)) {
      console.log('🔗 URL is not an app deep link, ignoring');
      return;
    }

    try {
      // Parse the deep link
      const { path, params } = parseDeepLink(url);
      console.log('🔗 Parsed deep link - Path:', path, 'Params:', params);

      // Create the event object
      const event: DeepLinkEvent = {
        url,
        path,
        params
      };

      // Find matching handlers
      let handlerFound = false;
      for (const [pattern, handlers] of this.handlers.entries()) {
        if (this.matchesPattern(path, pattern)) {
          console.log(`🔗 Found ${handlers.length} handler(s) for pattern: ${pattern}`);
          handlers.forEach(handler => {
            try {
              handler(event);
              handlerFound = true;
            } catch (error) {
              console.error('🔗 Error in deep link handler:', error);
            }
          });
        }
      }

      if (!handlerFound) {
        console.log('🔗 No handlers found for path:', path);
        // Default behavior: navigate to the path if it looks like an auth callback
        if (path.includes('auth/callback') || path.includes('callback')) {
          this.handleAuthCallback(event);
        }
      }
    } catch (error) {
      console.error('🔗 Error processing deep link:', error);
    }
  }

  /**
   * Check if a path matches a pattern
   * @param path - The actual path
   * @param pattern - The pattern to match against
   * @returns True if the path matches the pattern
   */
  private matchesPattern(path: string, pattern: string): boolean {
    // Simple pattern matching - can be enhanced with regex if needed
    if (pattern === '*') return true;
    if (pattern === path) return true;
    
    // Check if pattern is a prefix match
    if (pattern.endsWith('*')) {
      const prefix = pattern.slice(0, -1);
      return path.startsWith(prefix);
    }
    
    // Check if path contains the pattern
    return path.includes(pattern);
  }

  /**
   * Default handler for auth callback deep links
   * @param event - The deep link event
   */
  private async handleAuthCallback(event: DeepLinkEvent): Promise<void> {
    console.log('🔗 Handling auth callback deep link', event);
    console.log('🔗 Full URL received:', event.url);
    console.log('🔗 Parsed path:', event.path);
    console.log('🔗 Parsed params:', event.params);

    // Preserve referral parameters if present
    const referralParams = this.extractReferralParams(event.params);
    if (referralParams.ref) {
      console.log('🔗 Preserving referral code from deep link:', referralParams.ref);
      try {
        ReferralStorageService.storeReferralCode(
          referralParams.ref,
          referralParams.utm_campaign || 'deep_link'
        );
      } catch (error) {
        console.error('🔗 Error storing referral from deep link:', error);
      }
    }

    // Check if this is a session token callback from the web bridge
    const { access_token, refresh_token, token_type, expires_in } = event.params;

    if (access_token && refresh_token) {
      console.log('🔗 Session tokens detected from web bridge, establishing session...');

      try {
        // Set session directly with tokens from web bridge
        const { data, error } = await supabase.auth.setSession({
          access_token: decodeURIComponent(access_token),
          refresh_token: decodeURIComponent(refresh_token),
          token_type: token_type || 'bearer',
          expires_in: parseInt(expires_in) || 3600,
        });

        if (error) {
          console.error('🔗 Session creation failed:', error);
          window.location.href = '/auth/login?error=session_failed';
          return;
        }

        if (data.user) {
          console.log('🔗 Session created successfully for:', data.user.email);
          // Navigate to login page where user can sign in with their verified account
          window.location.href = '/auth/login';
          return;
        }
      } catch (error) {
        console.error('🔗 Exception during session creation:', error);
        window.location.href = '/auth/login?error=session_exception';
        return;
      }
    }

    // Fallback to existing parameter processing for other auth flows
    console.log('🔗 No session tokens found, using fallback parameter processing');
    const searchParams = new URLSearchParams(event.params);

    // Also check if there are any hash-style parameters that need to be preserved
    if (event.url.includes('#')) {
      const hashPart = event.url.split('#')[1];
      if (hashPart) {
        const hashParams = new URLSearchParams(hashPart);
        hashParams.forEach((value, key) => {
          searchParams.set(key, value);
        });
      }
    }

    const callbackUrl = `/auth/callback?${searchParams.toString()}`;
    console.log('🔗 Navigating to auth callback:', callbackUrl);

    // Use window.location to navigate
    window.location.href = callbackUrl;
  }

  /**
   * Extract referral-related parameters from deep link params
   * @param params - The deep link parameters
   * @returns Object containing referral parameters
   */
  private extractReferralParams(params: Record<string, string>): {
    ref?: string;
    utm_campaign?: string;
    utm_source?: string;
    utm_medium?: string;
  } {
    return {
      ref: params.ref || params.referral,
      utm_campaign: params.utm_campaign,
      utm_source: params.utm_source,
      utm_medium: params.utm_medium
    };
  }

  /**
   * Get initialization status
   */
  isServiceInitialized(): boolean {
    return this.isInitialized;
  }
}

// Export singleton instance
export const deepLinkHandler = new DeepLinkHandlerService();
